import { formatMoney } from '@makila/fiscal';
import { workTotal, type Order } from '@makila/pos-core';
import type { DeviceState } from './device';
import { esc, mzDate, mzDateTime } from './receipt';

/**
 * Ordens de serviço em papel térmico: o talão de entrega que o cliente leva
 * (não é factura) e, na lavandaria, uma senha por peça para prender à roupa.
 */

const style = (width: 58 | 80) => `<style>
  @page { size: ${width}mm auto; margin: 0; } * { box-sizing: border-box; }
  body { margin: 0; padding: 3mm ${width === 58 ? 2 : 4}mm; width: ${width}mm; font: ${width === 58 ? 10 : 11.5}px/1.3 ui-monospace, "Courier New", monospace; color: #000; }
  h1 { font-size: 1.25em; margin: 0; text-align: center; } .c { text-align: center; } .big { font-size: 2em; font-weight: 700; text-align: center; margin: 4px 0; }
  hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; } .tag { page-break-after: always; break-after: page; padding: 2mm 0; }
</style>`;

/** Talão de entrega: o número em grande, o que se deixou, o pedido e a data prometida. */
export function workSlipHtml(o: Order, device: DeviceState, opts: { width?: 58 | 80 } = {}): string {
  const w = o.work!;
  const c = device.company;
  const total = workTotal(o);
  return `<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>OS ${esc(w.number)}</title>${style(opts.width ?? 80)}</head><body>
    <h1>${esc(c.tradeName ?? c.legalName)}</h1>
    <div class="c">${esc([device.store.address || c.address, c.city].filter(Boolean).join(', '))}</div>
    <hr><div class="c"><b>ORDEM DE SERVIÇO</b></div>
    <div class="big">${esc(w.number)}</div>
    <div>Recebida: ${esc(mzDateTime(w.history[0]?.at ?? o.openedAt))}</div>
    <div>Cliente: ${esc(w.customer.name)}${w.customer.phone ? ` · ${esc(w.customer.phone)}` : ''}</div>
    <hr><div><b>${esc(w.object)}</b>${w.pieces ? ` · ${w.pieces} peça(s)` : ''}</div>
    ${w.identifier ? `<div>${esc(w.identifier)}</div>` : ''}
    ${w.problem ? `<div>${esc(w.problem)}</div>` : ''}
    ${w.promisedOn ? `<div>Pronta a: <b>${mzDate(w.promisedOn)}</b></div>` : ''}
    ${total !== '0.00' ? `<div>Valor previsto: ${formatMoney(total)} MT</div>` : ''}
    <hr><div class="c"><small>Apresente este talão para levantar. Não serve de factura: a factura é emitida na entrega.</small></div>
  </body></html>`;
}

/** Senhas da lavandaria: uma por peça ("3/8"), com o número da ordem e o cliente. */
export function workTagsHtml(o: Order, opts: { width?: 58 | 80 } = {}): string {
  const w = o.work!;
  const n = w.pieces ?? 1;
  const tags = Array.from({ length: n }, (_, i) => `<div class="tag"><div class="big">${esc(w.number)}</div><div class="c"><b>Peça ${i + 1}/${n}</b></div>
    <div class="c">${esc(w.customer.name)}</div>${w.promisedOn ? `<div class="c">Pronta a ${mzDate(w.promisedOn)}</div>` : ''}</div>`).join('');
  return `<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>Senhas ${esc(w.number)}</title>${style(opts.width ?? 58)}</head><body>${tags}</body></html>`;
}
